import { memo } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, Text } from '@frontend/design-system';

export interface HistoryGroupHeaderProps {
  /**
   * Determines whether the group header should behave as a dropdown.
   * - If `true`, the header will display a toggle icon (caret) and allow toggling the collapsed state.
   * - If `false`, the header will not display a toggle icon and will be non-interactive.
   */
  asDropdown?: boolean;
  /**
   * The label for the group (e.g., "March 2025").
   * This represents the grouping header text.
   */
  group: string;

  /**
   * Indicates whether the group is currently collapsed.
   * If true, the group content is hidden.
   */
  isCollapsed: boolean;

  /**
   * Indicates whether toggling the group is disabled.
   * If true, clicking the header will not trigger the toggle action.
   */
  isToggleDisabled: boolean;

  /**
   * Callback function triggered when the header is clicked.
   * Used to toggle the collapsed state of the group.
   */
  onToggle: () => void;
}

/**
 * `HistoryGroupHeader` component renders a collapsible group header.
 * It displays the group label and a toggle icon to expand or collapse the group.
 */
export const HistoryGroupHeader = memo(
  ({ group, isCollapsed, onToggle, asDropdown = false }: HistoryGroupHeaderProps) => (
    <NakedButton
      disabled={!asDropdown}
      onClick={onToggle}
      css={headerCss({ isCollapsed, asDropdown })}
      aria-expanded={!isCollapsed}
      aria-label={`Toggle group ${group}`}
    >
      <Text size='medium' color='light'>
        {group}
      </Text>
      {asDropdown && <Icon name={isCollapsed ? 'caret-down-small' : 'caret-up-small'} color='light' />}
    </NakedButton>
  )
);

HistoryGroupHeader.displayName = 'HistoryGroupHeader';

const headerCss = ({ isCollapsed, asDropdown }: { isCollapsed: boolean; asDropdown: boolean }) => css`
  width: 100%;
  display: flex;
  align-items: center;
  padding: ${theme.spacing(1, 2)};
  gap: ${theme.spacing(1)};
  border-bottom: ${isCollapsed ? `1px solid ${theme.colors.neutral20}` : 'none'};
  margin-top: ${theme.spacing(2)};
  cursor: ${asDropdown ? 'pointer' : 'default'};
`;
